import React, { useContext, useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { FormattedMessage } from 'react-intl'

import { CREDIT_CARD_PAYMENT_METHOD } from './constants'
import { typeCreditCard, typePaymentMethods } from 'client/types/checkout'

import {
  CONFIRM_BUTTON_SUBMIT_VARIANT,
  CHANGE_CREDIT_CARD_BUTTON_SUBMIT_VARIANT,
} from 'src/comps/JetIframeForm/constants'

import CreditCardInfo from 'src/comps/CreditCardInfo'
import PaymentOgone from 'src/comps/PaymentOgone'
import JetIframeForm from 'src/comps/JetIframeForm/container'
import PaymentWorldline from 'src/comps/PaymentWorldline'

import {
  CancelChangeCreditCardButton,
  ChangeCreditCardButton,
  ConfirmBookingSubmit,
  CreditCardFormContainer,
  SavedCreditCardSection,
} from './styles'

import { CheckoutContext } from 'client/components/CheckoutContainer/context'
import { PaymentMethodEnum } from 'client/contexts/Purchase/mappers'

const paymentFormsMap = {
  [PaymentMethodEnum.Ogone]: PaymentOgone,
  [PaymentMethodEnum.Paycomet]: JetIframeForm,
  [PaymentMethodEnum.Worldline]: PaymentWorldline,
}

const CreditCardForm = ({
  creditCard = {},
  isPrime,
  onError,
  onSuccess,
  onBeforeSubmit,
  paymentMethods,
  disabled,
  purchaseId,
  customSubmit,
}) => {
  const { isEditingCreditCard, setIsEditingCreditCard } = useContext(CheckoutContext) // keep the value in context
  const { url: iframeUrl, clientId } = paymentMethods.creditCard

  function hasSavedCreditCard() {
    return !!creditCard?.number
  }

  const onApproveCreditCard = useCallback(
    async paymentParams => {
      if (typeof onBeforeSubmit === 'function') {
        if (!(await onBeforeSubmit())) {
          return
        }
      }
      onSuccess(CREDIT_CARD_PAYMENT_METHOD, paymentParams)
    },
    [onBeforeSubmit, onSuccess]
  )

  function onSubmitSavedCreditCard(event) {
    event.preventDefault()
    onApproveCreditCard({ kind: paymentMethods.kind })
  }

  const paymentFormProps = useMemo(() => {
    const commonPaymentFormProps = {
      onSuccess: onApproveCreditCard,
      disabled,
      customSubmit,
      onBeforeSubmit,
      providerId: clientId,
      purchaseId,
    }

    const paymentFormPropsMap = {
      [PaymentMethodEnum.Ogone]: {
        ...commonPaymentFormProps,
        src: iframeUrl,
        onError,
      },
      [PaymentMethodEnum.Paycomet]: {
        ...commonPaymentFormProps,
        submitCopy: purchaseId
          ? CONFIRM_BUTTON_SUBMIT_VARIANT
          : CHANGE_CREDIT_CARD_BUTTON_SUBMIT_VARIANT,
      },
      [PaymentMethodEnum.Worldline]: {
        ...commonPaymentFormProps,
        src: iframeUrl,
        onError,
      },
    }

    return paymentFormPropsMap[paymentMethods.kind]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentMethods.kind, iframeUrl])

  const PaymentForm = paymentFormsMap[paymentMethods.kind]

  if (hasSavedCreditCard() && !isEditingCreditCard) {
    return (
      <SavedCreditCardSection data-testid="credit-card-saved">
        <CreditCardInfo
          brand={creditCard.brand}
          expiryDate={creditCard.expiryDate}
          holder={creditCard.holder}
          inputRadioWrapped
          number={creditCard.number}
          {...paymentFormProps}
        />
        <ChangeCreditCardButton
          onClick={() => setIsEditingCreditCard(true)}
          data-testid="change-credit-card"
        >
          <FormattedMessage id="global.change-credit-card" />
        </ChangeCreditCardButton>
        <ConfirmBookingSubmit
          onClick={onSubmitSavedCreditCard}
          isPrime={isPrime}
          disabled={disabled}
        >
          {isPrime ? (
            <FormattedMessage id="global.complete-purchase-prime" />
          ) : (
            <FormattedMessage id="global.confirm-booking" />
          )}
        </ConfirmBookingSubmit>
      </SavedCreditCardSection>
    )
  }

  return (
    <CreditCardFormContainer>
      {hasSavedCreditCard() && (
        <CancelChangeCreditCardButton onClick={() => setIsEditingCreditCard(false)}>
          <FormattedMessage id="global.cancel" />
        </CancelChangeCreditCardButton>
      )}
      <PaymentForm {...paymentFormProps} />
    </CreditCardFormContainer>
  )
}

CreditCardForm.propTypes = {
  creditCard: typeCreditCard,
  disabled: PropTypes.bool,
  isPrime: PropTypes.bool,
  onError: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  onBeforeSubmit: PropTypes.func,
  paymentMethods: typePaymentMethods,
  purchaseId: PropTypes.string,
  customSubmit: PropTypes.bool,
}

export default CreditCardForm
